import React from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  HStack,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";
import { TranslateIcon } from "@phosphor-icons/react";
import { LANGUAGE_KEY } from "../../helpers/constant";
import { useAuth } from "../../context/AuthContext";
import { updateUserProfile } from "../../services/auth";

const LanguageMenu: React.FC = () => {
  const { i18n, t } = useTranslation();
  const { userId } = useAuth();
  const toast = useToast();
  const textColor = useColorModeValue("black", "white");
  const [isUpdatingLang, setIsUpdatingLang] = React.useState(false);

  const languages = [
    {
      code: "fr",
      countryCode: "FR",
      label: t("common.languageFrench"),
    },
    {
      code: "en",
      countryCode: "GB",
      label: t("common.languageEnglish"),
    },
  ];

  const handleLanguageChange = async (lang: string) => {
    if (lang === i18n.language || isUpdatingLang) return;

    setIsUpdatingLang(true);
    try {
      if (userId) {
        await updateUserProfile(userId, { lang });
      }
      await i18n.changeLanguage(lang);
      localStorage.setItem(LANGUAGE_KEY, lang);
      toast({
        title: String(t("common.languageUpdateSuccessTitle")),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      toast({
        title: String(t("common.languageUpdateErrorTitle")),
        description:
          error instanceof Error ? error.message : String(t("common.tryAgain")),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsUpdatingLang(false);
    }
  };

  // Restore saved language on mount
  React.useEffect(() => {
    const savedLang = localStorage.getItem(LANGUAGE_KEY);
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  return (
    <Menu>
      <MenuButton
        as={IconButton}
        icon={
          isUpdatingLang ? <Spinner size="sm" /> : <TranslateIcon size={22} />
        }
        variant="ghost"
        p={1}
        isDisabled={isUpdatingLang}
        aria-label={String(t("common.changeLanguage"))}
      />

      <MenuList minW="140px">
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            isDisabled={isUpdatingLang}
          >
            <HStack spacing={3}>
              <ReactCountryFlag
                countryCode={lang.countryCode}
                svg
                style={{
                  width: "1.25em",
                  height: "1.25em",
                  borderRadius: "2px",
                }}
              />
              <Text color={textColor}>{lang.label}</Text>
            </HStack>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export default LanguageMenu;
